import type { ReactNode } from "react";
import { Button, cn } from "ui";
import { X } from "lucide-react";
import type { Translator } from "../types/nav";
import { useRewardsAdmin } from "../host/RewardsAdminHost";

export interface IFilterBarProps {
  /** Filter controls (search input, selects, date pickers). */
  children: ReactNode;
  /** When true, renders a subtle "clear filters" affordance. */
  hasActiveFilters?: boolean;
  /** Called when the clear affordance is pressed. */
  onClear?: () => void;
  t?: Translator;
  className?: string;
}

/**
 * A clean white card row that wraps a screen's search + filter controls so the
 * filters read as a single contained surface above the table.
 */
export function FilterBar({
  children,
  hasActiveFilters,
  onClear,
  t: tOverride,
  className,
}: IFilterBarProps) {
  const { t } = useRewardsAdmin({ t: tOverride });
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm",
        className,
      )}
    >
      {children}
      {hasActiveFilters && onClear && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          icon={<X className="h-3.5 w-3.5" />}
          className="ml-auto text-gray-500 hover:text-ticketon-dark-gray"
          data-testid="filter-clear"
        >
          {t("rewards.filter.clear")}
        </Button>
      )}
    </div>
  );
}
